import { EventEmitter, inject, Injectable } from '@angular/core';
import html2pdf from 'html2pdf.js';
import domtoimage from 'dom-to-image';
import { asBlob } from 'html-docx-js-typescript';
import { saveAs } from 'file-saver';
import { BehaviorSubject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { HostnameService } from '@services/hostname.service';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class PefAnalysisService {
  subTitleOnPDF = new EventEmitter();
  private document = inject(DOCUMENT);
  reportData: BehaviorSubject<any> = new BehaviorSubject(null);
  templateData: BehaviorSubject<any> = new BehaviorSubject(null);
  baseUrl: string;

  constructor(
    private hostnameService: HostnameService,
    private httpClient: HttpClient
  ) {
    this.baseUrl = this.hostnameService.getOrigin();
  }

  async export(id, filename, fileType, scoreAgGrid?, scenarioAgGrid?) {
    const chartID = ['app-gwp-chart', 'app-scores-compare'];
    try {
      this.replace(fileType, chartID, this.replaceChart.bind(this)).then(() => {
        if (fileType === 'pdf') this.toPDF(id, filename, fileType, chartID);
        if (fileType === 'docx') this.toDOC(id, filename, fileType, chartID, scoreAgGrid, scenarioAgGrid);
      });
    } catch (error) {
      console.log(error);
    }
  }

  replace(fileType, chartID, callback) {
    const chartPromises: any = [];
    chartID.forEach((chart) => {
      chartPromises.push(callback(chart, fileType));
    });
    return new Promise((resolve) => {
      Promise.all(chartPromises).then(() => {
        resolve(true);
      });
    });
  }

  async toPDF(id, filename, fileType?, chartID?) {
    this.subTitleOnPDF.emit(true);
    const el = this.document.querySelector('app-layout');
    const shadowRoot = el?.shadowRoot;
    const element = shadowRoot.getElementById(id);

    ['.light-theme-chip-label', '.chip-container'].forEach((selector) => {
      element.querySelectorAll(selector).forEach((e) => {
        (e as HTMLElement).style.overflow = 'visible';
        (e as HTMLElement).style.whiteSpace = 'normal';
        (e as HTMLElement).style.display = 'flex';
      });
    });
    ['.dropdown-menu__left', '.dropdown-menu__text', '.dropdown-menu'].forEach((selector) => {
      element.querySelectorAll(selector).forEach((e) => {
        (e as HTMLElement).style.overflow = 'visible';
        (e as HTMLElement).style.whiteSpace = 'normal';
        (e as HTMLElement).style.display = 'flex';
      });
    });
    const opt = {
      margin: [0.6, 0.1],
      filename: filename.replace(/[^\w -]/g, '_').replace(/__*/g, '_'),
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2, backgroundColor: null },
      pagebreak: { before: '#pdfPage2', avoid: ['article', 'div'] },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    await html2pdf().set(opt).from(element).save();
    await this.replace(fileType, chartID, this.revertChart.bind(this));
  }

  toDOC(id, filename, fileType?, chartID?, scoreAgGrid?, scenarioAgGrid?) {
    const el = this.document.querySelector('app-layout');
    const shadowRoot = el?.shadowRoot;
    const element = shadowRoot.getElementById(id);
    const opt = {
      margins: { top: 1080, right: 1080, bottom: 1080, left: 1080 },
      orientation: 'landscape' as const
    };
    const style = ``;
    if (element) {
      let ele = `<html lang="en"><head><meta charset="utf-8"><style>${style}</style></head><body>${element.outerHTML}</body></html>`;
      const tempElement = document.createElement('div');
      tempElement.innerHTML = ele;
      ['desc', '.highcharts-legend-item', 'app-score-compare-table', '#pef-score-grid'].forEach((selector) => {
        tempElement.querySelectorAll(selector).forEach((e) => {
          e.innerHTML = '';
        });
      });
      tempElement.querySelectorAll('#pef-score-grid').forEach((e) => {
        e.innerHTML = `${scoreAgGrid}`;
      });
      tempElement.querySelectorAll('app-score-compare-table').forEach((e) => {
        e.innerHTML = `${scenarioAgGrid}`;
      });
      ele = tempElement.innerHTML;
      asBlob(ele, opt).then((doc) => {
        saveAs(doc, filename);
        this.replace(fileType, chartID, this.revertChart.bind(this));
      });
    }
  }

  async replaceChart(id, fileType) {
    const el = this.document.querySelector('app-layout');
    const shadowRoot = el?.shadowRoot;
    if (!shadowRoot.querySelector(id)) return;
    const node = shadowRoot.querySelector(id).parentElement;
    if (fileType === 'pdf' && node.attributes['data-missing']?.nodeValue === 'false') {
      return;
    }
    if (fileType === 'docx' && (id === 'app-gwp-chart' || id === 'app-scores-compare')) {
      node.style.transform = 'scale(0.6)';
      node.style['transform-origin'] = 'top left';
    }
    if (fileType === 'pdf' && (id === 'app-gwp-chart' || id === 'app-scores-compare')) {
      node.style.transform = 'scale(0.8)';
      node.style['transform-origin'] = 'top left';
    }

    await new Promise((resolve) => {
      domtoimage
        .toPng(node, {})
        .then((dataUrl) => {
          const img = new Image();
          img.src = dataUrl;
          node.appendChild(img);
          (node.children[1] as HTMLElement).style.display = 'none';
          resolve(true);
        })
        .catch((error) => {
          console.error(`oops, something went wrong with ${id}!`, error);
        });
    });
  }

  revertChart(id, fileType) {
    const el = this.document.querySelector('app-layout');
    const shadowRoot = el?.shadowRoot;
    if (!shadowRoot.querySelector(id)) return;
    const node = shadowRoot.querySelector(id).parentElement;
    if (fileType === 'docx' || fileType === 'pdf') node.style.transform = 'scale(1)';
    if (fileType === 'pdf' && node.attributes['data-missing']?.nodeValue === 'false') {
      return;
    }
    (node.children[1] as HTMLElement).style.display = 'inline';
    node.removeChild(node.children[1]);
  }

  getPEF(rid: string) {
    rid = rid.replace('#', '');
    return this.httpClient.get(`${this.baseUrl}/api/v1/pef/${rid}`);
  }

  getLCBreakdowns(rid: string) {
    rid = rid.replace('#', '');
    return this.httpClient.get(`${this.baseUrl}/api/v1/pef/portlet/lc_breakdown/${rid}`);
  }

  getMaterialBreakdowns(rid: string) {
    rid = rid.replace('#', '');
    return this.httpClient.get(`${this.baseUrl}/api/v1/pef/portlet/material_breakdown/${rid}`);
  }

  getScoreComparison(pefRid: string, pcrRid: string, rids: string[] = []) {
    pefRid = pefRid.replace('#', '');
    let params = `pcr=${pcrRid}`;
    if (rids?.length) {
      params = `${params}&rids=${rids.join('&rids=').replace(/#/g, '')}`;
    }
    return this.httpClient.get(`${this.baseUrl}/api/v1/pef/portlet/score_comparison/${pefRid}?${params}`);
  }
}
